/**
 * StudentGuide: the students' visual guide (help id "student"): finding an exam, starting it, answering it (the
 * question list, the flag, the timer), submitting it, the result and the review, and My attempts. Opened by the "?"
 * after the titles of Available exams, My attempts and an exam's page (ExamDetail). Not inside the running exam:
 * there Esc, the panel's close key, would also leave fullscreen, which the exam counts against the student.
 * It follows the reader's screen: under 768px (ExamSession's breakpoint for its side list, and My attempts' cards)
 * the phone layout: the question list behind the button at the top left, Submit and "⋯" in a row of their own.
 * Props, from the page: `computer` (the exam asks this device for fullscreen: no touch screen and a fullscreen API,
 * as ExamSession decides); it adds the fullscreen sentence of the start step, the Esc warning and the keyboard tip.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  AcademicCapIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  FlagIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpLegend, HelpNote, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  Mark,
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDialog,
  MiniField,
  MiniIcon,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniTable,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";

// The numbers of the exam's question list (QuestionNavPanel) at the Mini size: Mini.js has no part for them. Its
// tokens: 24px squares with 11px text on phones, 28px with 12px from md up; the list's own colours.
const NUMBER_LOOKS = {
  current: "bg-blue-600 text-white shadow-sm shadow-blue-600/30",
  answered: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  open: "bg-white text-slate-600 ring-1 ring-inset ring-slate-200",
};

function QuestionNumber({ n, look = "open", flagged = false, mark = false }) {
  const box = (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-medium tabular-nums md:h-7 md:w-7 md:text-xs ${
        NUMBER_LOOKS[look] || NUMBER_LOOKS.open
      } ${flagged ? "outline outline-2 outline-offset-1 outline-amber-400" : ""}`}
    >
      {n}
    </span>
  );
  if (!mark) return box;
  return (
    <Mark pointer round="md" label={typeof mark === "string" ? mark : undefined}>
      {box}
    </Mark>
  );
}

// A sample list: 1-2 answered, 3 on screen, 4 flagged, the rest open.
const LOOKS = { 1: "answered", 2: "answered", 3: "current" };

export default function StudentGuide({ computer = false }) {
  const [t] = useTranslation();
  // ExamSession's own breakpoint: the side list and the "⋯" look from md up; My attempts' table too.
  const wide = useMediaQuery("(min-width: 768px)");
  const ex = (key) => t(`help.common.example.${key}`);

  const submit = t("exam_taking.submit_exam");
  const timer = (tone, time) => (
    <MiniBadge icon={ClockIcon} tone={tone}>
      {time}
    </MiniBadge>
  );
  // The phones' first header row with its question list button marked; the window's title names the exam (its
  // name beside the button would crowd the marker).
  const phoneHeader = (listMark) => (
    <MiniRow className="justify-between gap-y-3">
      <MiniButton variant="quiet" icon={Squares2X2Icon} mark={listMark} />
      {timer("slate", "00:24:58")}
    </MiniRow>
  );
  const numbers = (from, to, markAt) => (
    <MiniRow className="gap-y-3">
      {Array.from({ length: to - from + 1 }, (_, index) => {
        const n = from + index;
        return <QuestionNumber key={n} n={n} look={LOOKS[n]} flagged={n === 4} mark={n === markAt && (wide ? true : "2")} />;
      })}
    </MiniRow>
  );

  const examCard = (name, minutes, tests, score, mark = false) => (
    <MiniCard icon={AcademicCapIcon} mark={mark}>
      <MiniText variant="strong" truncate>
        {name}
      </MiniText>
      <MiniRow>
        <MiniRow className="flex-nowrap">
          <MiniIcon icon={ClockIcon} />
          <MiniText variant="muted">{t("exam_taking.minutes_value", { minutes })}</MiniText>
        </MiniRow>
        <MiniRow className="flex-nowrap">
          <MiniIcon icon={ListBulletIcon} />
          <MiniText variant="muted">{t("exam_taking.tests_count", { count: tests })}</MiniText>
        </MiniRow>
        <MiniRow className="flex-nowrap">
          <MiniIcon icon={CheckBadgeIcon} />
          <MiniText variant="muted">{`${t("exam_taking.passing_score")}: ${score}%`}</MiniText>
        </MiniRow>
      </MiniRow>
    </MiniCard>
  );

  // An answer option as the exam draws it: its letter, its text, blue with a tick once chosen.
  const option = (letter, text, chosen = false) => (
    <MiniCard
      key={letter}
      tone={chosen ? "blue" : "white"}
      icon={<MiniBadge tone={chosen ? "blue" : "slate"}>{letter}</MiniBadge>}
      aside={chosen ? <MiniIcon icon={CheckCircleIcon} tone="blue" /> : null}
      mark={chosen}
    >
      {text}
    </MiniCard>
  );

  const attemptRows = [
    { key: "graded", name: ex("exam"), number: 2, status: t("exam_taking.status_graded"), tone: "green" },
    { key: "running", name: ex("exam_2"), number: 1, status: t("exam_taking.status_in_progress"), tone: "blue" },
  ];
  const attemptAction = (row) =>
    row.key === "graded" ? (
      <MiniButton variant="link" mark>
        {t("exam_taking.view_result")}
      </MiniButton>
    ) : (
      <MiniButton variant="primary">{t("exam_taking.continue_exam")}</MiniButton>
    );

  return (
    <>
      <HelpIntro>{t("help.student.intro")}</HelpIntro>

      <HelpFlow pipeline="exams" current="attempts" />

      <HelpSteps>
        <HelpStep
          title={t("help.student.steps.find.title")}
          illustration={
            <MiniScreen title={t("exam_taking.available_exams")}>
              <MiniRow>
                <MiniField icon={MagnifyingGlassIcon} placeholder={t("exam_taking.search_placeholder")} className="flex-1" />
              </MiniRow>
              {examCard(ex("exam"), 110, 3, 70, true)}
              {examCard(ex("exam_2"), 150, 1, 50)}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.student.steps.find.text" values={{ page: t("exam_taking.available_exams") }} />
          <p>
            <HelpText i18nKey="help.student.steps.find.text_score" values={{ score: t("exam_taking.passing_score") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.start.title")}
          illustration={
            <MiniScreen title={ex("exam")}>
              <MiniRow>
                <MiniBadge icon={ClockIcon}>{t("exam_taking.minutes_value", { minutes: 110 })}</MiniBadge>
                <MiniBadge icon={ListBulletIcon}>{t("question_list.count", { count: 60 })}</MiniBadge>
                <MiniBadge icon={CheckBadgeIcon}>{`${t("exam_taking.passing_score")}: 70%`}</MiniBadge>
              </MiniRow>
              <MiniCard tone="amber">
                <MiniRow>
                  <MiniText variant="caps">{t("exam_taking.exam_rules")}</MiniText>
                </MiniRow>
                <MiniRow>
                  <MiniText>{t("exam_taking.rule_tab")}</MiniText>
                </MiniRow>
                {/* As on the page, the agreement is a white row of its own: its marker's bubble stays off the rule. */}
                <MiniCard>
                  <MiniCheck checked mark="1">
                    {t("exam_taking.rules_acknowledge")}
                  </MiniCheck>
                </MiniCard>
              </MiniCard>
              <MiniRow>
                <MiniButton variant="primary" mark="2">
                  {t("exam_taking.start_exam")}
                </MiniButton>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey="help.student.steps.start.text"
            values={{ agree: t("exam_taking.rules_acknowledge"), start: t("exam_taking.start_exam") }}
          />
          {/* Computers get the fullscreen screen before the first question (ExamSession's GateScreen). */}
          {computer && (
            <p>
              <HelpText i18nKey="help.student.steps.start.text_computer" values={{ fullscreen: t("exam_taking.enter_fullscreen_start") }} />
            </p>
          )}
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.answer.title")}
          illustration={
            <MiniStack>
              <MiniText variant="muted">{`${t("exam_taking.question")} 3 / 10`}</MiniText>
              <MiniCard>
                <MiniRow className="justify-between">
                  <MiniButton variant="quiet" icon={FlagIcon} />
                  <MiniBadge>{t("exam_taking.points_value", { count: 1 })}</MiniBadge>
                </MiniRow>
                <MiniText variant="strong">{t("help.student.example.question")}</MiniText>
                {option("A", "54")}
                {option("B", "56", true)}
                {option("C", "64")}
              </MiniCard>
              <MiniRow className="justify-between">
                <MiniButton variant="quiet" icon={ChevronLeftIcon}>
                  {t("buttons.back")}
                </MiniButton>
                <MiniButton variant="primary">{t("buttons.next")}</MiniButton>
              </MiniRow>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.student.steps.answer.text" values={{ next: t("buttons.next"), back: t("buttons.back") }} />
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.list.title")}
          illustration={
            <MiniScreen title={ex("exam")}>
              {!wide && phoneHeader("1")}
              {/* As QuestionNavPanel draws it: "Show flagged" above the tests, each under its caption; on phones in a
                  drawer named after the list. */}
              <MiniCard>
                {!wide && <MiniText variant="strong">{t("exam_taking.question_nav")}</MiniText>}
                <MiniRow>
                  <MiniBadge tone="amber" icon={FlagIcon}>{`${t("exam_taking.show_flagged")} (1)`}</MiniBadge>
                </MiniRow>
                <MiniText variant="caps">{ex("subject")}</MiniText>
                {/* The marked number is in the last row: its cursor hangs into the card's padding. */}
                <MiniStack>
                  {numbers(1, 5)}
                  {numbers(6, 10, 7)}
                </MiniStack>
              </MiniCard>
            </MiniScreen>
          }
        >
          {/* Paragraphs, so the step's spacing comes between them (the text has no pill to carry it). */}
          <p>{wide ? t("help.student.steps.list.text") : t("help.student.steps.list.text_phone")}</p>
          <p>
            <HelpText i18nKey="help.student.steps.list.text_flag" values={{ flagged: t("exam_taking.show_flagged") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.finish.title")}
          illustration={
            <MiniStack>
              <MiniScreen title={ex("exam")}>
                {wide ? (
                  <MiniRow className="justify-between gap-y-3">
                    <MiniText variant="strong" truncate>
                      {ex("exam")}
                    </MiniText>
                    <MiniRow className="gap-y-3">
                      {timer("amber", "00:04:59")}
                      <MiniButton variant="primary" mark="1">
                        {submit}
                      </MiniButton>
                      <MiniButton variant="quiet" icon={EllipsisHorizontalIcon} />
                    </MiniRow>
                  </MiniRow>
                ) : (
                  <>
                    <MiniRow className="justify-between">
                      <MiniRow className="flex-nowrap">
                        <MiniButton variant="quiet" icon={Squares2X2Icon} />
                        <MiniText variant="strong" truncate>
                          {ex("exam")}
                        </MiniText>
                      </MiniRow>
                      {timer("amber", "00:04:59")}
                    </MiniRow>
                    <MiniRow className="flex-nowrap gap-y-3">
                      <MiniButton variant="primary" mark="1">
                        {submit}
                      </MiniButton>
                      <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />
                    </MiniRow>
                  </>
                )}
              </MiniScreen>
              <MiniDialog
                title={submit}
                footer={[
                  <MiniButton key="cancel">{t("buttons.cancel")}</MiniButton>,
                  <MiniButton key="submit" variant="primary" mark="2">
                    {submit}
                  </MiniButton>,
                ]}
              >
                <MiniText variant="strong">{t("exam_taking.confirm_submit")}</MiniText>
                <MiniCard tone="amber">{t("exam_taking.unanswered_warning", { answered: 9, total: 10 })}</MiniCard>
              </MiniDialog>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.student.steps.finish.text" values={{ submit }} />
          <p>
            <HelpText i18nKey="help.student.steps.finish.text_exit" values={{ exit: t("exam_taking.exit_exam") }} />
          </p>
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.result.title")}
          illustration={
            <MiniScreen title={t("exam_taking.result")}>
              <MiniRow>
                <MiniText variant="title">72.5%</MiniText>
                <MiniBadge tone="green" icon={CheckCircleIcon}>
                  {t("exam_taking.passed")}
                </MiniBadge>
              </MiniRow>
              <MiniRow>
                <MiniBadge tone="green">{`${t("exam_taking.correct_answers")}: 29`}</MiniBadge>
                <MiniBadge tone="red">{`${t("exam_taking.incorrect_answers")}: 11`}</MiniBadge>
              </MiniRow>
              <MiniRow className="gap-y-3">
                <MiniButton variant="primary" mark>
                  {t("exam_taking.review")}
                </MiniButton>
              </MiniRow>
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey="help.student.steps.result.text"
            values={{ passed: t("exam_taking.passed"), failed: t("exam_taking.failed"), review: t("exam_taking.review") }}
          />
          <p>{t("help.student.steps.result.text_colors")}</p>
        </HelpStep>

        <HelpStep
          title={t("help.student.steps.attempts.title")}
          illustration={
            wide ? (
              <MiniTable
                columns={[t("exam_taking.exam"), { label: t("exam_taking.status"), width: "30%" }, { label: "", width: "34%", align: "right" }]}
                rows={attemptRows.map((row) => [
                  <MiniText variant="strong" truncate>
                    {row.name}
                  </MiniText>,
                  <MiniBadge tone={row.tone}>{row.status}</MiniBadge>,
                  attemptAction(row),
                ])}
              />
            ) : (
              // My attempts' phone cards: the status at the top right, the action at the bottom right.
              <MiniStack>
                {attemptRows.map((row) => (
                  <MiniCard key={row.key}>
                    <MiniRow className="justify-between">
                      <MiniText variant="strong" truncate>
                        {row.name}
                      </MiniText>
                      <MiniBadge tone={row.tone}>{row.status}</MiniBadge>
                    </MiniRow>
                    <MiniRow>
                      <MiniText variant="muted">{`${t("exam_taking.attempt_number")}${row.number}`}</MiniText>
                    </MiniRow>
                    <MiniRow className="justify-end gap-y-3">{attemptAction(row)}</MiniRow>
                  </MiniCard>
                ))}
              </MiniStack>
            )
          }
        >
          <HelpText
            i18nKey="help.student.steps.attempts.text"
            values={{ page: t("exam_taking.my_attempts"), result: t("exam_taking.view_result"), continue: t("exam_taking.continue_exam") }}
          />
        </HelpStep>
      </HelpSteps>

      <HelpNote tone="warn">
        {t("help.student.notes.leave")}
        {/* In fullscreen the browser takes Esc for itself and leaves fullscreen, which the exam counts; elsewhere Esc
            closes menus and dialogs, so students reach for it. */}
        {computer && (
          <>
            {" "}
            <HelpText i18nKey="help.student.notes.esc" values={{ cancel: t("buttons.cancel") }} />
          </>
        )}
      </HelpNote>

      <HelpLegend
        title={t("help.student.legend.title")}
        rows={[
          { key: "current", badge: <QuestionNumber n={3} look="current" />, text: t("help.student.legend.current") },
          { key: "answered", badge: <QuestionNumber n={2} look="answered" />, text: t("help.student.legend.answered") },
          { key: "open", badge: <QuestionNumber n={5} />, text: t("help.student.legend.open") },
          { key: "flagged", badge: <QuestionNumber n={4} flagged />, text: t("help.student.legend.flagged") },
          {
            key: "passage",
            badge: (
              <MiniBadge tone="indigo" icon={BookOpenIcon}>
                {t("exam_taking.passage.nav_caption", { from: 6, to: 8 })}
              </MiniBadge>
            ),
            text: t("help.student.legend.passage"),
          },
        ]}
      />

      <HelpTips
        items={[
          // Only a computer has the keys (ExamSession's shortcuts).
          computer && t("help.student.tips.keys"),
          <HelpText
            key="tools"
            i18nKey="help.student.tips.tools"
            values={{ calculator: t("exam_taking.calculator.title"), table: t("exam_taking.periodic_table.title") }}
          />,
          t("help.student.tips.breaks"),
          <HelpText key="later" i18nKey="help.student.tips.later" values={{ hidden: t("exam_taking.results_hidden_title") }} />,
        ]}
      />
    </>
  );
}
